import { useEffect, useRef } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import { MaskedLine } from "./Reveal";

const PageHero = ({ eyebrow, title, italic, intro, image, overlayStrength = 0.65, children, testId }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.14]);

  // Lock body padding (fallback)
  useEffect(() => {}, []);

  return (
    <section
      ref={ref}
      data-testid={testId || "page-hero"}
      className="relative overflow-hidden bg-brand-night pt-[76px] text-white"
    >
      <motion.img
        src={image}
        alt=""
        aria-hidden
        style={{ y, scale }}
        className="absolute inset-0 h-[115%] w-full object-cover will-change-transform"
      />
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, rgba(6,9,26,${overlayStrength * 0.55}) 0%, rgba(6,9,26,${overlayStrength * 0.25}) 40%, rgba(6,9,26,${overlayStrength * 1.1}) 100%), linear-gradient(90deg, rgba(6,9,26,${overlayStrength * 0.75}) 0%, rgba(6,9,26,${overlayStrength * 0.2}) 60%, rgba(6,9,26,0) 100%)`,
        }}
      />
      <img
        aria-hidden
        src="/b-mark.png"
        alt=""
        className="pointer-events-none absolute right-[-3rem] bottom-[-3rem] w-[220px] select-none opacity-[0.08] rotate-6 sm:w-[320px] lg:right-[-5rem] lg:bottom-[-5rem] lg:w-[440px]"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="min-h-[62vh] flex flex-col justify-end py-20 sm:py-28 lg:min-h-[64vh] lg:py-32">
          {eyebrow && (
            <MaskedLine delay={0.1}>
              <span className="inline-flex items-center gap-3 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-brand-gold">
                <span className="h-px w-10 bg-brand-gold" />
                {eyebrow}
              </span>
            </MaskedLine>
          )}
          <h1 className="editorial-h mt-8 max-w-4xl text-[2.4rem] font-medium leading-[1.04] text-white sm:text-[3.4rem] lg:text-[4.2rem]">
            <MaskedLine delay={0.22}>{title}</MaskedLine>
            {italic && (
              <MaskedLine delay={0.35}>
                <span className="italic text-white/70">{italic}</span>
              </MaskedLine>
            )}
          </h1>
          {intro && (
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
            >
              {intro}
            </motion.p>
          )}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.72 }}
              className="mt-10"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
